import React, { Fragment, useRef, useState } from 'react';
import {
    Popover,
    IconButton,
    MenuList,
    MenuItem,
    Paper,
} from '@material-ui/core';
import {
    Archive,
    Bell,
    BellOff,
    FileText,
    Info,
    Lock,
    Tag,
    Unlock,
    XCircle,
} from 'react-feather';
import { MoreVert } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useWarboxConversation } from 'src/hooks';
import useAuth from 'src/hooks/useAuth';
import { SnackbarAlert } from 'src/components/Alerts/components/System/components/SnackbarAlert';
import { UPDATE_PERMISSION, TAGS_AUTH } from 'src/constants/permissions';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import {
    ARCHIVED_CONVERSATION_STATUS,
    BLOCKED_CONVERSATION_STATUS,
    OPEN_CONVERSATION_STATUS,
} from '../../constants/conversationStatus';
import { AddConversationTagsModal } from './AddConversationTagsModal';
import type { ConversationTag } from '../../types';
import { DEVELOPER_LEVEL_ACCESS } from 'src/components/access';

export const HeaderOptionsMenu = ({
    conversationId,
    conversationStatus,
    storeId,
    currentTags,
    isSilenced = false,
    onOpenDetails,
    onOpenNotes,
    onConversationStatusUpdated,
    onCloseChat,
}: {
    conversationId?: string;
    conversationStatus?: string;
    storeId?: string;
    currentTags?: ConversationTag[];
    isSilenced?: boolean;
    onOpenDetails?: () => void;
    onOpenNotes?: () => void;
    onConversationStatusUpdated?: (status: string) => void;
    onCloseChat?: () => void;
}) => {
    const [open, setOpen] = useState(false);
    const [openTagsModal, setOpenTagsModal] = useState(false);
    const { updateConversation } = useWarboxConversation();
    const { user } = useAuth();
    const { t } = useTranslation();
    const { customAlert } = SnackbarAlert();
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const isBlocked = conversationStatus === BLOCKED_CONVERSATION_STATUS;
    const isArchived = conversationStatus === ARCHIVED_CONVERSATION_STATUS;
    const isDeveloper = user?.accessLevel === DEVELOPER_LEVEL_ACCESS;
    const canUpdateTags = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: TAGS_AUTH,
        crud: UPDATE_PERMISSION,
    });

    const handleToggleOpen = () => setOpen(!open);

    const handleUpdateConversationStatus = async (status: string) => {
        if (!conversationId || conversationStatus === status) return;

        await updateConversation({ status }, conversationId);
        onConversationStatusUpdated?.(status);
    };

    const handleToggleSilence = async () => {
        if (!conversationId) return;

        await updateConversation({ isSilenced: !isSilenced }, conversationId);
        customAlert(
            isSilenced
                ? t('Chat.NotificationsUnmuted')
                : t('Chat.NotificationsMuted'),
            'success'
        );
    };

    const options = [
        {
            icon: <Info style={{ fontSize: 20 }} />,
            label: t('Chat.ChatInformation'),
            onClick: onOpenDetails,
            showIf: true,
        },
        {
            icon: <FileText style={{ fontSize: 20 }} />,
            label: t('Chat.ViewNotes'),
            onClick: onOpenNotes,
            showIf: true,
        },
        {
            icon: <Tag style={{ fontSize: 20 }} />,
            label: t('Chat.AddTag'),
            onClick: () => setOpenTagsModal(true),
            showIf: Boolean(conversationId) && canUpdateTags,
        },
        {
            icon: <Archive style={{ fontSize: 20 }} />,
            label: t('Chat.ArchiveConversation'),
            onClick: () =>
                handleUpdateConversationStatus(ARCHIVED_CONVERSATION_STATUS),
            showIf: Boolean(conversationId) && !isArchived,
        },
        {
            icon: <Archive style={{ fontSize: 20 }} />,
            label: t('Chat.UnarchiveConversation'),
            onClick: () =>
                handleUpdateConversationStatus(OPEN_CONVERSATION_STATUS),
            showIf: Boolean(conversationId) && isArchived,
        },
        {
            icon: isSilenced ? (
                <Bell style={{ fontSize: 20 }} />
            ) : (
                <BellOff style={{ fontSize: 20 }} />
            ),
            label: isSilenced
                ? t('Chat.UnmuteNotifications')
                : t('Chat.MuteNotifications'),
            onClick: handleToggleSilence,
            showIf: Boolean(conversationId),
        },
        {
            icon: <Lock style={{ fontSize: 20 }} />,
            label: t('Chat.BlockConversation'),
            onClick: () =>
                handleUpdateConversationStatus(BLOCKED_CONVERSATION_STATUS),
            showIf: Boolean(conversationId) && !isBlocked,
        },
        {
            icon: <Unlock style={{ fontSize: 20 }} />,
            label: t('Chat.UnblockConversation'),
            onClick: () =>
                handleUpdateConversationStatus(OPEN_CONVERSATION_STATUS),
            showIf: Boolean(conversationId) && isBlocked && isDeveloper,
        },
        {
            icon: <XCircle style={{ fontSize: 20 }} />,
            label: t('Chat.CloseChat'),
            onClick: onCloseChat,
            showIf: Boolean(conversationId),
        },
    ];

    const handleOptionClick = (onClick?: () => void) => {
        setOpen(false);
        if (onClick) onClick();
    };

    return (
        <Fragment>
            <Popover
                open={open}
                onClose={handleToggleOpen}
                anchorEl={buttonRef.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Paper>
                    <MenuList>
                        {options
                            .filter((option) => option.showIf)
                            .map((option, index) => (
                                <MenuItem
                                    key={index}
                                    onClick={() =>
                                        handleOptionClick(option.onClick)
                                    }
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                    }}
                                >
                                    {option.icon} {option.label}
                                </MenuItem>
                            ))}
                    </MenuList>
                </Paper>
            </Popover>

            <IconButton
                color="primary"
                onClick={handleToggleOpen}
                ref={buttonRef}
            >
                <MoreVert />
            </IconButton>

            <AddConversationTagsModal
                open={openTagsModal}
                setOpen={setOpenTagsModal}
                conversationId={conversationId}
                storeId={storeId}
                currentTags={currentTags}
            />
        </Fragment>
    );
};
